import { useDispatch } from "react-redux";
import { singleOrderDetailsGenericCallForPostgres } from "../api/postgreSQL/postCalls/singleOrderDetailsGenericCallForPostgres";
import { ReviewData } from "../data-model/reviewModel";
import { ModalId } from "../enums/modalId";
import { IconNames } from "../enums/orderStatusIcons";
import { SingleOrderDetailsTableName } from "../enums/singleOrderDetailsTableName";
import { setMetaData, setOpenedModalId } from "../redux/slice/modalSlice";
import { AppDispatch } from "../redux/store/store";
import { getOrgIdForFetchExternalWorklist } from "../utils/organizationHelper";

/**
 * Custom hook to handle clicks on review-related icons in overlapping icons cells.
 *
 * When ORDER_REVIEW_APPROVED or ORDER_REVIEW_REJECTED icons are clicked,
 * this hook fetches the latest review data and opens the ViewReviewModal.
 *
 * @returns A function that handles icon clicks: (icon_id, order_id, abortControllerRef?) => Promise<void>
 *
 * @example
 * const handleIconClick = useReviewIconClick();
 *
 * // In your table component:
 * <CustomTable
 *   onOverlappingIconClick={handleIconClick}
 *   ...
 * />
 */
export const useReviewIconClick = () => {
  const dispatch = useDispatch<AppDispatch>();

  const handleIconClick = async (
    icon_id: string,
    order_id: string,
    abortControllerRef?: React.MutableRefObject<AbortController | null>,
  ) => {
    if (
      icon_id === IconNames.ORDER_REVIEW_APPROVED ||
      icon_id === IconNames.ORDER_REVIEW_REJECTED
    ) {
      abortControllerRef?.current?.abort();
      const controller = new AbortController();
      if (abortControllerRef) {
        abortControllerRef.current = controller;
      }
      try {
        dispatch(
          setOpenedModalId({
            id: ModalId.VIEW_REVIEW_MODAL,
            metaData: {
              reviewData: null,
              isLoading: true,
            },
          }),
        );
        const allReviews = (await singleOrderDetailsGenericCallForPostgres(
          {
            order_id: order_id,
            org_id: getOrgIdForFetchExternalWorklist(),
            table_name: SingleOrderDetailsTableName.REVIEWS,
          },
          controller.signal,
        )) as { reviews: ReviewData[] };

        const latestReviewSortedByCreatedAt = allReviews?.reviews?.sort(
          (a, b) =>
            new Date(b.created_at ?? "").getTime() -
            new Date(a.created_at ?? "").getTime(),
        )[0];
        dispatch(
          setMetaData({
            reviewData: latestReviewSortedByCreatedAt,
            isLoading: false,
          }),
        );
      } catch (error) {
        if (controller.signal.aborted) return;
        dispatch(
          setOpenedModalId({
            id: ModalId.VIEW_REVIEW_MODAL,
            metaData: {
              reviewData: null,
              isLoading: false,
            },
          }),
        );
      }
    } else {
      return;
    }
  };

  return handleIconClick;
};
